import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Protected document reader for course PDFs.
//
// What it does: downloads the PDF with the user's login (the server refuses anyone else),
// draws every page onto a canvas (so there is no text to select or copy and no PDF file for
// the browser's own viewer, which has Save and Print), stamps the reader's name into each
// page image, and blocks print/save/copy shortcuts and the right-click menu.
//
// What it cannot do: no website can stop a screenshot or a photo of the screen. The name
// stamped into every page is there so a leaked copy can be traced to the person who took it.

const WORKER_SOURCES = [
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js',
  'https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js',
];
pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_SOURCES[0];

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2.0;

function readSession(sessionKey) {
  try {
    return JSON.parse(sessionStorage.getItem(sessionKey)) || {};
  } catch (e) {
    return {};
  }
}

// A light but dense stamp: two staggered lines of text tiled over the whole page, so it is
// easy to read around yet cannot be cropped away without cutting out the content.
function stampWatermark(canvas, text) {
  const ctx = canvas.getContext('2d');
  const w = canvas.width;
  const h = canvas.height;
  const size = Math.max(12, Math.round(w / 52));
  const gapX = size * 2.2;
  const stepY = size * 3.4;
  ctx.save();
  ctx.globalAlpha = 0.075;
  ctx.fillStyle = '#111827';
  ctx.font = `600 ${size}px sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const textW = ctx.measureText(text).width + gapX;
  ctx.translate(w / 2, h / 2);
  ctx.rotate(-Math.PI / 9);
  const reach = Math.hypot(w, h);
  let row = 0;
  for (let y = -reach; y < reach; y += stepY, row += 1) {
    const shift = (row % 2) * (textW / 2);
    for (let x = -reach - shift; x < reach; x += textW) {
      ctx.fillText(text, x, y);
    }
  }
  ctx.restore();
}

const ctlStyle = (disabled) => ({
  background: '#ffffff',
  color: '#1f2937',
  border: '1px solid #94a3b8',
  borderRadius: 6,
  padding: '4px 12px',
  fontSize: 14,
  lineHeight: 1.4,
  minWidth: 44,
  opacity: disabled ? 0.45 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

function SecurePdfReader({ pdfUrl, sessionKey = 'studentSession', onBack }) {
  const session = useMemo(() => readSession(sessionKey), [sessionKey]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [error, setError] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [obscured, setObscured] = useState(false);
  const ackKey = `pdfNoticeAck:${sessionKey}:${session.id || session.username || ''}`;
  const [acknowledged, setAcknowledged] = useState(() => {
    try { return localStorage.getItem(ackKey) === '1'; } catch (e) { return false; }
  });
  const acknowledge = () => {
    try { localStorage.setItem(ackKey, '1'); } catch (e) { /* ignore */ }
    setAcknowledged(true);
  };
  const pdfRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRefs = useRef({});
  const renderedRef = useRef({}); // page -> zoom it was drawn at
  const renderingRef = useRef({});

  const watermark = useMemo(() => {
    const name = [session.firstName, session.lastName].filter(Boolean).join(' ') || session.username || 'Registered user';
    const id = session.username || session.id || '';
    const now = new Date();
    return `${name} · ${id} · ${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }, [session]);

  // ---- load the document -------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    let doc = null;
    (async () => {
      try {
        if (!session.token) throw new Error('Please log in again to read this document.');
        const response = await fetch(pdfUrl, {
          headers: { Authorization: `Bearer ${session.token}`, Accept: 'application/pdf' },
          cache: 'no-store',
        });
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error('You do not have access to this document.');
          }
          throw new Error('This document could not be loaded. Please try again.');
        }
        const bytes = new Uint8Array(await response.arrayBuffer());
        let lastErr = null;
        for (const src of WORKER_SOURCES) {
          try {
            pdfjsLib.GlobalWorkerOptions.workerSrc = src;
            doc = await pdfjsLib.getDocument({ data: bytes.slice() }).promise;
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            if (!/worker/i.test(String(err && err.message))) break;
          }
        }
        if (lastErr) throw lastErr;
        if (cancelled) return;
        pdfRef.current = doc;
        setPageCount(doc.numPages);
        setStatus('ready');
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'This document could not be loaded.');
          setStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
      if (doc) doc.destroy();
      pdfRef.current = null;
    };
  }, [pdfUrl, session.token]);

  // ---- draw a page (with the reader's name stamped in) ----------------------------
  const drawPage = useCallback(async (n) => {
    const pdf = pdfRef.current;
    const canvas = canvasRefs.current[n];
    if (!pdf || !canvas || renderingRef.current[n] || renderedRef.current[n] === zoom) return;
    renderingRef.current[n] = true;
    try {
      const page = await pdf.getPage(n);
      const box = containerRef.current;
      const available = Math.max(280, (box ? box.clientWidth : 800) - 24);
      const base = page.getViewport({ scale: 1 });
      const cssScale = (available / base.width) * zoom;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: cssScale * ratio });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
      canvas.style.height = `${Math.floor(viewport.height / ratio)}px`;
      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      stampWatermark(canvas, watermark);
      renderedRef.current[n] = zoom;
    } catch (err) {
      /* a failed page stays blank; the rest still render */
    } finally {
      renderingRef.current[n] = false;
    }
  }, [zoom, watermark]);

  // Draw pages as they come near the screen, and track which page is showing.
  useEffect(() => {
    if (status !== 'ready' || !acknowledged) return undefined;
    const box = containerRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const n = Number(e.target.dataset.page);
          if (e.isIntersecting) drawPage(n);
        });
      },
      { root: box, rootMargin: '600px 0px', threshold: [0, 0.35, 0.6] }
    );
    Object.values(canvasRefs.current).forEach((c) => c && io.observe(c));
    return () => io.disconnect();
  }, [status, acknowledged, pageCount, zoom, drawPage]);

  // The page number follows the scroll position: the last page whose top has passed
  // roughly the upper third of the reading area.
  const rafRef = useRef(0); // pending timer id
  const updateCurrentPage = useCallback(() => {
    if (rafRef.current) return; // one update per short interval while scrolling
    rafRef.current = setTimeout(() => {
      rafRef.current = 0;
      const box = containerRef.current;
      if (!box) return;
      const boxTop = box.getBoundingClientRect().top;
      const line = box.clientHeight * 0.35;
      let found = 1;
      for (let n = 1; n <= pageCount; n += 1) {
        const canvas = canvasRefs.current[n];
        if (!canvas) continue;
        if (canvas.getBoundingClientRect().top - boxTop <= line) found = n;
        else break;
      }
      setCurrentPage(found);
    }, 60);
  }, [pageCount]);
  useEffect(() => () => clearTimeout(rafRef.current), []);

  // After a zoom change every page needs redrawing at the new size.
  useEffect(() => {
    renderedRef.current = {};
  }, [zoom]);

  // ---- protection ----------------------------------------------------------------
  useEffect(() => {
    const block = (e) => { e.preventDefault(); return false; };
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      const devtools = e.key === 'F12' || (mod && e.shiftKey && ['i', 'j', 'c'].includes(k)) || (mod && k === 'u');
      if ((mod && ['p', 's', 'c', 'a', 'x'].includes(k)) || devtools || e.key === 'PrintScreen') {
        e.preventDefault();
        e.stopPropagation();
        if (e.key === 'PrintScreen' && navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText('Protected document').catch(() => {});
        }
        return false;
      }
      return undefined;
    };
    const hide = () => setObscured(true);
    const onVisibility = () => { if (document.visibilityState === 'hidden') setObscured(true); };

    // Printing: show nothing but a notice.
    const style = document.createElement('style');
    style.setAttribute('data-secure-pdf', 'true');
    style.textContent = `@media print { body * { display: none !important; } body::before { content: "Printing is disabled for protected documents."; display: block !important; font: 20px sans-serif; padding: 40px; } }`;
    document.head.appendChild(style);

    document.addEventListener('contextmenu', block);
    document.addEventListener('copy', block);
    document.addEventListener('cut', block);
    document.addEventListener('dragstart', block);
    document.addEventListener('selectstart', block);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('beforeprint', hide);
    window.addEventListener('blur', hide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('copy', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('dragstart', block);
      document.removeEventListener('selectstart', block);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('beforeprint', hide);
      window.removeEventListener('blur', hide);
      document.removeEventListener('visibilitychange', onVisibility);
      style.remove();
    };
  }, []);

  const zoomBy = (delta) => setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((z + delta) * 100) / 100)));

  const pages = useMemo(() => Array.from({ length: pageCount }, (_, i) => i + 1), [pageCount]);

  return (
    <div style={{ userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}>
      <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#7c2d12', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
        <i className="ph ph-shield-warning me-2" />
        <strong>Confidential course material.</strong> Your name and ID are marked on every page and each viewing is recorded.
        Copying, photographing, screen-capturing or sharing this document is prohibited and may lead to removal from the course.
      </div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-8 mb-12">
        <div className="d-flex align-items-center gap-8">
          {status === 'ready' && <span className="badge bg-success">Page {currentPage} of {pageCount}</span>}
          <span className="text-12 text-gray-500"><i className="ph ph-lock-simple me-1" />Protected document: viewing only</span>
        </div>
        <div className="d-flex align-items-center gap-8">
          <div className="d-flex align-items-center gap-4">
            <button type="button" style={ctlStyle(status !== 'ready' || zoom <= MIN_ZOOM)} disabled={status !== 'ready' || zoom <= MIN_ZOOM} onClick={() => zoomBy(-0.2)} title="Zoom out" aria-label="Zoom out">
              <i className="ph ph-magnifying-glass-minus" /> <span>−</span>
            </button>
            <span style={{ minWidth: 56, textAlign: 'center', fontSize: 14, color: '#1f2937', fontWeight: 600 }}>{Math.round(zoom * 100)}%</span>
            <button type="button" style={ctlStyle(status !== 'ready' || zoom >= MAX_ZOOM)} disabled={status !== 'ready' || zoom >= MAX_ZOOM} onClick={() => zoomBy(0.2)} title="Zoom in" aria-label="Zoom in">
              <i className="ph ph-magnifying-glass-plus" /> <span>+</span>
            </button>
          </div>
          {onBack && <button type="button" style={ctlStyle(false)} onClick={onBack}>Back</button>}
        </div>
      </div>

      {status === 'loading' && <div className="text-center py-40"><div className="spinner-border text-main-600" /><div className="text-13 text-gray-500 mt-8">Opening document…</div></div>}
      {status === 'error' && <div className="alert alert-danger">{error}</div>}

      {status === 'ready' && !acknowledged && (
        <div className="card border-0 shadow-sm p-20 text-center">
          <h5 className="mb-8">Before you read</h5>
          <p className="text-14 text-gray-600 mb-16">
            This material is confidential and for your own study only. It is marked with your name and every viewing is recorded.
            Do not copy, photograph, screen-capture or share it.
          </p>
          <div><button type="button" className="btn btn-main rounded-pill px-24" onClick={acknowledge}>I understand, open the document</button></div>
        </div>
      )}

      {status === 'ready' && acknowledged && (
        <div style={{ position: 'relative' }}>
          <div
            ref={containerRef}
            onScroll={updateCurrentPage}
            style={{
              height: '78vh', overflowY: 'auto', background: '#e5e7eb', borderRadius: 8, padding: 12,
              filter: obscured ? 'blur(22px)' : 'none', transition: 'filter .15s',
            }}
          >
            {pages.map((n) => (
              <div key={n} style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <canvas
                  data-page={n}
                  ref={(el) => { canvasRefs.current[n] = el; }}
                  style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.25)', pointerEvents: 'none', width: '100%', minHeight: 300 }}
                />
              </div>
            ))}
          </div>
          {obscured && (
            <button
              type="button"
              onClick={() => setObscured(false)}
              style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,.55)', color: '#fff', border: 0, borderRadius: 8, fontSize: 16, cursor: 'pointer' }}
            >
              The document is hidden while this window is not in focus.<br />Click here to continue reading.
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default SecurePdfReader;
